import { useState } from "react";
import { PageHeader } from "../../components/shared/PageHeader";
import { Badge } from "../../components/shared/Badge";
import { Plus, Lock, Copy, Trash2, Save } from "lucide-react";
import { cn } from "@nthamizh/ui";
import { useToast } from "@nthamizh/ui";
import { usePlatformRoles, useCreateRole, useUpdateRole, useDeleteRole } from "@platform/hooks";

interface RolePermissions {
  [moduleKey: string]: Record<string, boolean>;
}

interface PlatformRole {
  id: string;
  key: string;
  name: string;
  description: string | null;
  type: "system" | "custom";
  moduleName: string | null;
  individuallyAssignable: boolean;
  permissions: RolePermissions;
}

const MODULE_PERMS: Record<string, { key: string; label: string }[]> = {
  design: [
    { key: "task_instance_create",    label: "Task instance — create" },
    { key: "task_instance_generate",  label: "Task instance — generate" },
    { key: "task_history_view",       label: "Task history — view" },
    { key: "task_history_enterprise", label: "Task history — enterprise" },
  ],
  cost_simulator: [
    { key: "view_simulate",    label: "Simulate — run costing visualizer, eligibility & combinations" },
    { key: "view_interagency", label: "Interagency — view costing combinations interagency data" },
    { key: "manage_data",      label: "Manage data — upload & sync costing source tables" },
  ],
  admin: [
    { key: "manage_enterprise", label: "Manage enterprise settings" },
    { key: "manage_users",      label: "Manage users" },
    { key: "manage_roles",      label: "Manage roles" },
    { key: "manage_modules",    label: "Manage module access" },
  ],
};

// Task Template — Admin and Task History — Admin are intentionally absent
// from design above. Neither is ever a role permission — both come exclusively
// from the platform_admin flag on a user's account.

const MODULE_LABELS: Record<string, string> = {
  design:         "ConfigIQ",
  cost_simulator: "Cost Simulator",
  admin:          "Admin",
};

const MODULE_COLORS: Record<string, string> = {
  design:         "bg-indigo-600",
  cost_simulator: "bg-emerald-600",
  admin:          "bg-gray-500",
};

/** Returns only the module permission sections relevant to a role.
 *  Module-scoped roles (CostSim User, ConfigIQ User) show only their own
 *  module. Cross-module roles (Enterprise admin, Read only) show all modules
 *  that have permissions declared in MODULE_PERMS. */
function getVisibleModules(role: PlatformRole): string[] {
  // role.moduleName is actually the module key from the API (e.g. "cost_simulator")
  const moduleKey = role.moduleName;
  if (!moduleKey) {
    // Cross-module role — show every module in the permission matrix
    return Object.keys(MODULE_PERMS);
  }
  // Module-scoped role — show only its own module section
  return MODULE_PERMS[moduleKey] ? [moduleKey] : Object.keys(MODULE_PERMS);
}

export function PlatformRoleBuilderPage() {
  const { data: roles = [], isLoading } = usePlatformRoles();
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();
  const deleteRole = useDeleteRole();
  const { toast } = useToast();

  const typedRoles = roles as PlatformRole[];
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [editedPerms, setEditedPerms] = useState<RolePermissions>({});
  const [editedName, setEditedName] = useState("");
  const [editedIndividuallyAssignable, setEditedIndividuallyAssignable] = useState(false);
  const [hasEdits, setHasEdits] = useState(false);

  const selectedRole = typedRoles.find((r) => r.id === selectedId) ?? null;
  const isSystem = selectedRole?.type === "system";

  const selectRole = (role: PlatformRole) => {
    setSelectedId(role.id);
    setEditedPerms(JSON.parse(JSON.stringify(role.permissions)));
    setEditedName(role.name);
    setEditedIndividuallyAssignable(role.individuallyAssignable);
    setHasEdits(false);
  };

  const togglePerm = (moduleKey: string, permKey: string) => {
    if (isSystem) return;
    setEditedPerms((prev) => ({
      ...prev,
      [moduleKey]: { ...prev[moduleKey], [permKey]: !prev[moduleKey]?.[permKey] },
    }));
    setHasEdits(true);
  };

  const handleToggleIndividuallyAssignable = async () => {
    if (!selectedRole) return;
    const next = !editedIndividuallyAssignable;
    setEditedIndividuallyAssignable(next); // optimistic
    try {
      await updateRole.mutateAsync({ id: selectedRole.id, individuallyAssignable: next });
      toast({
        title: next
          ? `${selectedRole.name} can now be assigned without an enterprise`
          : `${selectedRole.name} no longer assignable without an enterprise`,
        variant: "success",
      });
    } catch {
      setEditedIndividuallyAssignable(!next); // revert
      toast({ title: "Failed to update", variant: "error" });
    }
  };

  const handleSave = async () => {
    if (!selectedRole) return;
    try {
      await updateRole.mutateAsync({
        id: selectedRole.id,
        name: editedName,
        permissions: editedPerms,
      });
      setHasEdits(false);
      toast({ title: `${editedName} saved`, variant: "success" });
    } catch {
      toast({ title: "Failed to save role", variant: "error" });
    }
  };

  const handleDuplicate = async () => {
    if (!selectedRole) return;
    try {
      await createRole.mutateAsync({
        key: `${selectedRole.key}_copy_${Date.now()}`,
        name: `${selectedRole.name} (copy)`,
        description: selectedRole.description ?? undefined,
      });
      toast({ title: `Duplicated as "${selectedRole.name} (copy)"`, variant: "success" });
    } catch {
      toast({ title: "Failed to duplicate role", variant: "error" });
    }
  };

  const handleDelete = async () => {
    if (!selectedRole || isSystem) return;
    try {
      await deleteRole.mutateAsync(selectedRole.id);
      setSelectedId(null);
      toast({ title: `${selectedRole.name} deleted`, variant: "success" });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to delete role";
      toast({ title: msg, variant: "error" });
    }
  };

  const handleCreate = async () => {
    if (!newRoleName.trim()) return;
    const key = newRoleName.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
    try {
      await createRole.mutateAsync({ key, name: newRoleName });
      setNewRoleName("");
      setShowNewForm(false);
      toast({ title: `"${newRoleName}" created`, variant: "success" });
    } catch {
      toast({ title: "Failed to create role", variant: "error" });
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Role builder"
        subtitle="Define roles and granular permissions. Enterprise admins assign these to users."
      />

      <div className="flex gap-5 items-start">
        {/* Role list */}
        <div className="w-52 flex-shrink-0 bg-white border border-ds-border rounded-card shadow-card overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-ds-border">
            <p className="text-xs font-semibold text-ds-text">Roles</p>
            <button
              onClick={() => setShowNewForm((v) => !v)}
              className="w-6 h-6 rounded-lg bg-indigo-600 flex items-center justify-center text-white hover:bg-indigo-700"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>

          {showNewForm && (
            <div className="p-3 border-b border-ds-border bg-ds-bg space-y-2">
              <input
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                placeholder="Role name"
                className="w-full px-2.5 py-1.5 text-xs border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400"
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
              />
              <div className="flex gap-1.5">
                <button
                  onClick={handleCreate}
                  disabled={createRole.isPending || !newRoleName.trim()}
                  className="flex-1 py-1 rounded-lg bg-indigo-600 text-white text-xs font-medium disabled:opacity-50"
                >
                  Create
                </button>
                <button
                  onClick={() => { setShowNewForm(false); setNewRoleName(""); }}
                  className="flex-1 py-1 rounded-lg border border-ds-border text-xs text-ds-text-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="p-4 text-center text-xs text-ds-text-muted">Loading...</div>
          ) : (
            <div className="divide-y divide-ds-border">
              {typedRoles.map((r) => (
                <button
                  key={r.id}
                  onClick={() => selectRole(r)}
                  className={cn(
                    "w-full text-left px-4 py-3 transition-colors border-l-2",
                    selectedId === r.id
                      ? "bg-indigo-50 border-l-indigo-600"
                      : "hover:bg-ds-bg border-l-transparent",
                  )}
                >
                  <p className={cn("text-xs font-medium truncate", selectedId === r.id ? "text-indigo-700" : "text-ds-text")}>
                    {r.name}
                  </p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <div className={cn("w-1.5 h-1.5 rounded-full", r.type === "system" ? "bg-gray-400" : "bg-purple-500")} />
                    <p className="text-xs text-ds-text-muted capitalize">{r.type}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Editor */}
        <div className="flex-1 min-w-0">
          {!selectedRole ? (
            <div className="bg-white border border-ds-border rounded-card shadow-card p-12 text-center text-ds-text-muted text-sm">
              Select a role to view or edit its permissions
            </div>
          ) : (
            <div className="bg-white border border-ds-border rounded-card shadow-card p-5 space-y-4">
              {/* Header */}
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    {isSystem ? (
                      <h2 className="text-sm font-semibold text-ds-text">{selectedRole.name}</h2>
                    ) : (
                      <input
                        value={editedName}
                        onChange={(e) => { setEditedName(e.target.value); setHasEdits(true); }}
                        className="text-sm font-semibold text-ds-text border-b border-transparent focus:border-indigo-400 focus:outline-none bg-transparent"
                      />
                    )}
                    <Badge variant={isSystem ? "gray" : "purple"}>{isSystem ? "System" : "Custom"}</Badge>
                  </div>
                  <p className="text-xs text-ds-text-muted">{selectedRole.description}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={handleDuplicate}
                    disabled={createRole.isPending}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 text-xs font-medium disabled:opacity-50"
                  >
                    <Copy className="w-3 h-3" /> Duplicate
                  </button>
                  {!isSystem && (
                    <>
                      <button
                        onClick={handleDelete}
                        disabled={deleteRole.isPending}
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 text-xs font-medium disabled:opacity-50"
                      >
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                      {hasEdits && (
                        <button
                          onClick={handleSave}
                          disabled={updateRole.isPending}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 text-xs font-medium disabled:opacity-50"
                        >
                          <Save className="w-3 h-3" /> Save
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {isSystem && (
                <div className="flex items-start gap-2.5 p-3 rounded-lg bg-ds-bg border border-ds-border">
                  <Lock className="w-3.5 h-3.5 text-ds-text-muted mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-ds-text-secondary">
                    System roles are read-only. Duplicate to create a customisable version.
                  </p>
                </div>
              )}

              {/* Editable regardless of isSystem, unlike name/permissions
                  above — this is a scoping decision (where can this role
                  be assigned), not a capability edit, so locking it
                  behind "duplicate the role first" would be unnecessary
                  friction for a single boolean. Auto-saves immediately
                  on toggle rather than joining the name/permissions
                  hasEdits/Save flow, which stays exactly as
                  system-role-restricted as before this change. */}
              <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-ds-border">
                <div>
                  <p className="text-xs font-medium text-ds-text">Assignable without an enterprise</p>
                  <p className="text-xs text-ds-text-muted mt-0.5">
                    Lets a platform admin assign this role to individual users who aren&apos;t part of any
                    enterprise (roles panel on their user detail page). Applies globally across every
                    enterprise tenant when assigned this way — see the docs on the user detail page.
                  </p>
                </div>
                <button
                  role="switch"
                  aria-checked={editedIndividuallyAssignable}
                  onClick={handleToggleIndividuallyAssignable}
                  disabled={updateRole.isPending}
                  className={cn(
                    "relative flex-shrink-0 w-9 h-5 rounded-full transition-colors disabled:opacity-50",
                    editedIndividuallyAssignable ? "bg-indigo-600" : "bg-gray-200",
                  )}
                >
                  <span
                    className={cn(
                      "absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform",
                      editedIndividuallyAssignable ? "translate-x-4" : "translate-x-0",
                    )}
                  />
                </button>
              </div>

              {/* Permission matrix — shows only modules relevant to this role.
                  Module-scoped roles (CostSim User, ConfigIQ User) show only
                  their own module. Cross-module roles (Enterprise admin, Read
                  only) show all modules they have declared permissions for. */}
              {getVisibleModules(selectedRole).map((moduleKey) => {
                const perms = MODULE_PERMS[moduleKey];
                if (!perms) return null;
                return (
                <div key={moduleKey} className="border border-ds-border rounded-xl overflow-hidden">
                  <div className="flex items-center gap-2.5 px-4 py-2.5 bg-ds-bg border-b border-ds-border">
                    <div className={cn("w-5 h-5 rounded flex items-center justify-center", MODULE_COLORS[moduleKey] ?? "bg-gray-400")}>
                      <div className="w-2 h-2 rounded-sm bg-white opacity-90" />
                    </div>
                    <p className="text-xs font-semibold text-ds-text">{MODULE_LABELS[moduleKey] ?? moduleKey}</p>
                  </div>
                  <div className="grid grid-cols-2">
                    {perms.map((p, i) => {
                      const checked = editedPerms[moduleKey]?.[p.key] ?? false;
                      return (
                        <button
                          key={p.key}
                          onClick={() => togglePerm(moduleKey, p.key)}
                          disabled={isSystem}
                          className={cn(
                            "flex items-center gap-2.5 px-4 py-2.5 text-left border-b border-ds-border transition-colors",
                            i % 2 === 0 ? "border-r border-ds-border" : "",
                            isSystem ? "cursor-default" : "hover:bg-ds-bg",
                          )}
                        >
                          <div className={cn(
                            "w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors",
                            checked ? "bg-indigo-600 border-indigo-600" : "border-ds-border-strong",
                            isSystem ? "opacity-60" : "",
                          )}>
                            {checked && (
                              <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <span className="text-xs text-ds-text">{p.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
